import React from 'react';

import type {
  ComponentRenderProps,
  ListProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';
import { renderTextWithIcons } from '../utils/emojiToIcon';

/**
 * Coerces a list item to its display string. Items are normally plain
 * strings, but AI-authored manifests sometimes emit bare scalars or
 * `{ text: "..." }` shaped objects — this tolerates those shapes instead
 * of crashing. Mirrors the backend's `list` normalization in
 * apps/reasoning/services/dynamic_view_components.py — keep in sync.
 */
function listItemToString(item: ListProps['items'][number]): string | null {
  if (item === null || item === undefined) {
    console.warn(
      'ListComponent: list item has no recognized text/content/label key',
      typeof item,
      []
    );
    return null;
  }
  if (typeof item === 'string') {
    return item;
  }
  if (typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }
  const text = item.text ?? item.content ?? item.label;
  if (text === undefined) {
    console.warn(
      'ListComponent: list item has no recognized text/content/label key',
      typeof item,
      Object.keys(item)
    );
    return null;
  }
  return text;
}

export const ListComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as ListProps;
  const {
    items,
    type = 'unordered',
    style = 'default',
    spacing = 'normal',
  } = props;
  const manifestId = useManifestId();

  // Build CSS classes
  const spacingClasses = {
    tight: 'space-y-1',
    normal: 'space-y-2',
    loose: 'space-y-3',
  };

  const styleClasses = {
    default:
      type === 'ordered'
        ? 'list-decimal list-outside pl-5'
        : 'list-disc list-outside pl-5',
    none: 'list-none',
    disc: 'list-disc list-outside pl-5',
    decimal: 'list-decimal list-outside pl-5',
  };

  // Using design system tokens for dark mode support
  const allClasses = [
    styleClasses[style] || styleClasses.default,
    spacingClasses[spacing] || spacingClasses.normal,
    'text-fg-primary text-[14px]',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const itemClasses = 'leading-relaxed';

  // Validate data
  if (!items || !Array.isArray(items) || items.length === 0) {
    return (
      <div className='border border-yellow-300 bg-yellow-50 p-4 rounded'>
        <div className='text-yellow-800 font-medium'>No list items</div>
        <div className='text-yellow-600 text-sm'>
          No items provided for the list
        </div>
      </div>
    );
  }

  const ListTag = type === 'ordered' ? 'ol' : 'ul';

  return (
    <ListTag className={allClasses} data-component-id={component.id}>
      {(() => {
        const seen = new Map<string, number>();
        return items.map(item => {
          const text = listItemToString(item);
          if (text === null) {
            return null;
          }
          const count = seen.get(text) ?? 0;
          seen.set(text, count + 1);
          return (
            <li key={`${text}::${count}`} className={itemClasses}>
              {renderTextWithIcons(
                text,
                'w-4 h-4 inline-block relative -top-[2px] mr-1',
                manifestId
              )}
            </li>
          );
        });
      })()}
    </ListTag>
  );
};

export default ListComponent;
